import React, { useEffect } from 'react';
import { getYouTubeEmbedUrl } from '../utils/youtube';
import './ProductVideoModal.css';

interface ProductVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl?: string;
  productName: string;
}

export default function ProductVideoModal({
  isOpen,
  onClose,
  videoUrl,
  productName
}: ProductVideoModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !videoUrl) return null;

  const embedUrl = getYouTubeEmbedUrl(videoUrl);

  return (
    <div className="pvm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pvm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pvm-header">
          <div className="pvm-title-wrap">
            <span className="pvm-tag">🎆 LIVE CRACKER DEMONSTRATION</span>
            <h3 className="pvm-title">{productName}</h3>
          </div>
          <button 
            className="pvm-close-btn" 
            onClick={onClose} 
            title="Close video (Esc)"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Video Player */}
        <div className="pvm-player-wrapper">
          {embedUrl ? (
            <iframe
              src={embedUrl}
              title={`${productName} demo video`}
              className="pvm-iframe"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="pvm-fallback">
              <p>Unable to load video embed.</p>
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pvm-external-link"
              >
                Watch directly on YouTube &rarr;
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pvm-footer">
          <p className="pvm-hint">
            Watch how this firecracker bursts, illuminates the sky, and performs!
          </p>
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pvm-yt-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Open in YouTube
          </a>
        </div>
      </div>
    </div>
  );
}
